import React from "react";
import { Odds } from "@picklebet/ui";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { OutcomeIcon } from "@/features/shell/components/shared/OutcomeIcon";
import { BetRow } from "@/features/betslip/components/parts/BetRow";
import { useOddsFormat } from "@/hooks/firestore/user/useOddsFormat";
import { toOutcomeType } from "@/features/betslip/utils/selectionHelpers";
import { isSingleSelection } from "@/features/betslip/utils/typeGuards";
import type { BetSelection, SemSelection } from "@/features/betslip/types";

import * as styles from "@/features/betslip/components/parts/MultiLegRow.module.scss";

type MultiLegRowProps = {
  bet: Exclude<BetSelection, SemSelection>;
  radio: React.ReactNode;
};

/**
 * A non-SEM leg row on the multi card. Subscribes to its own leg's pending
 * odds so only the leg that moved re-renders. While a change is pending the
 * committed price shows struck through with the incoming price beside it — the
 * stake-strip treatment without the direction arrow or colour.
 */
export const MultiLegRow = ({ bet, radio }: MultiLegRowProps) => {
  const pendingOdds = useBetslipStore((s) => s.oddsChanges[bet.outcomeId]);
  // No price to show on a live leg this user can't bet, pending or not.
  const isLiveViewOnly = useBetslipStore((s) => s.liveViewOnly[bet.outcomeId]);
  // Once the multi is placed, show the price the server took for this leg.
  const acceptedOdds = useBetslipStore((s) =>
    s.multiResult?.status === "accepted"
      ? s.multiResult.oddsByLeg?.[bet.outcomeId]
      : undefined,
  );
  const odds = acceptedOdds ?? bet.odds;
  const format = useOddsFormat();

  const isSingle = isSingleSelection(bet);
  const competitor = isSingle ? bet.competitor : undefined;
  const outcomeType =
    isSingle && bet.kind !== "race" ? bet.outcomeType : undefined;
  const silksUrl = isSingle && bet.kind === "race" ? bet.silksUrl : undefined;
  const selectionName = isSingle ? bet.selectionName : bet.eventName;

  const market = bet.isSP ? (
    `${bet.marketName} @ SP`
  ) : isLiveViewOnly ? (
    `${bet.marketName} @ -`
  ) : pendingOdds !== undefined && pendingOdds !== odds ? (
    <>
      {bet.marketName} @{" "}
      <span className={styles.oddsChange}>
        <span className={styles.previousOdds} data-testid="leg-previous-odds">
          <Odds base={odds} format={format} zero="1.00" />
        </span>
        <span className={styles.newOdds} data-testid="leg-new-odds">
          <Odds base={pendingOdds} format={format} zero="1.00" />
        </span>
      </span>
    </>
  ) : (
    <>
      {bet.marketName} @{" "}
      <Odds
        base={odds}
        format={format}
        zero="1.00"
        className={styles.legOdds}
      />
    </>
  );

  return (
    <BetRow
      icon={
        <OutcomeIcon
          outcomeType={toOutcomeType(outcomeType)}
          competitor={competitor}
          silksUrl={silksUrl}
          size={24}
          reserveAvatarSpace
        />
      }
      name={selectionName}
      market={market}
      rightSlot={radio}
      testId={`multi-bet-row-${bet.outcomeId}`}
    />
  );
};
